// Home.js

import { useState, useEffect } from 'react';

// Importa le immagini (assicurati che i percorsi siano corretti)
// Esempio: se sono in src/assets, usa './assets/logo-monastero.png'
import logoMonastero from './logo_monastero.png';
import cardinaleEmoji from './cardinale-emoji.png';

// Chiave per salvare il cardinale in localStorage
const SAVED_CARDINALE_STORAGE_KEY = 'monasteroWifiSavedCardinale';

function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cardinale, setCardinale] = useState(null); // Cardinale attualmente mostrato nel modale
  const [savedCardinale, setSavedCardinale] = useState(null); // Cardinale salvato in localStorage

  const [totalPrayers, setTotalPrayers] = useState(0);
  const [displayedPrayers, setDisplayedPrayers] = useState(0);
  const [loading, setLoading] = useState(false); // Usato per il bottone di conferma preghiera
  const [error, setError] = useState(null); // Usato per errori generali (es. fetch preghiere)
  const [confirmed, setConfirmed] = useState(false); // Stato per la conferma della preghiera nel modale

  // Nuovo stato per memorizzare i cardinali caricati dinamicamente
  const [cardinali, setCardinali] = useState([]);
  // Nuovo stato per indicare il caricamento dei cardinali
  const [loadingCardinali, setLoadingCardinali] = useState(true);
  const [errorLoadingCardinali, setErrorLoadingCardinali] = useState(null);

  // Nuovo stato per gestire l'apertura/chiusura del menu off-canvas
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // --- URL dei tuoi script PHP ---
  // DOVRAI SOSTITUIRE QUESTI URL CON QUELLI REALI SUL TUO SERVER
  // Assicurati che questi percorsi siano corretti rispetto alla root del tuo sito o alla sottocartella della tua app
  const GET_PRAYERS_URL = '/adotta-un-cardinale/api/get_prayers.php'; // Esempio: URL per ottenere il conteggio
  const INCREMENT_PRAYERS_URL = '/adotta-un-cardinale/api/increment_prayers.php'; // Esempio: URL per incrementare il conteggio
  // -------------------------------


  // Effetto 1: Carica i cardinali dal file JSON E controlla se c'è un cardinale salvato in localStorage
  useEffect(() => {
    const fetchData = async () => {
      try {
        // --- Caricamento Cardinali ---
        // Utilizza fetch per caricare il file dalla root della cartella public
        // Assicurati che il percorso sia corretto per la tua sottocartella, se applicabile
        const response = await fetch('/adotta-un-cardinale/cardinali.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setCardinali(data); // Salva i dati nello stato del componente

        // --- Caricamento Cardinale Salvato da localStorage ---
        const savedCardinaleString = localStorage.getItem(SAVED_CARDINALE_STORAGE_KEY);
        if (savedCardinaleString) {
          try {
            const parsedCardinale = JSON.parse(savedCardinaleString);
            // Opzionale: verifica che il cardinale salvato esista ancora nell'elenco caricato
            // Questa verifica è utile se l'elenco cardinali.json può cambiare
            const isValid = data.some(c => c.nome === parsedCardinale.nome && c.cognome === parsedCardinale.cognome); // o confronta per un ID univoco se disponibile
            if (isValid) {
               setSavedCardinale(parsedCardinale);
            } else {
                console.warn("Cardinale salvato in localStorage non trovato nell'elenco corrente. Rimozione...");
                localStorage.removeItem(SAVED_CARDINALE_STORAGE_KEY); // Rimuove se non valido
            }
          } catch (parseError) {
            console.error("Errore durante il parsing del cardinale salvato da localStorage:", parseError);
            localStorage.removeItem(SAVED_CARDINALE_STORAGE_KEY); // Rimuove se il formato non è corretto
          }
        }

        setLoadingCardinali(false); // Imposta lo stato di caricamento a falso
      } catch (e) {
        console.error("Errore nel caricamento dei dati iniziali (cardinali o salvataggio):", e);
        setErrorLoadingCardinali("Impossibile caricare i dati necessari.");
        setLoadingCardinali(false); // Imposta lo stato di caricamento a falso anche in caso di errore
        // Potresti voler disabilitare i bottoni anche qui se il caricamento fallisce
      }
    };

    fetchData(); // Chiama la funzione di fetch all'avvio del componente

    // L'array vuoto come dipendenza assicura che questo effetto
    // venga eseguito una sola volta al "mount" del componente.
  }, []);

  // Effetto 2: Carica il numero totale di preghiere dal backend all'avvio
  useEffect(() => {
    const fetchTotalPrayers = async () => {
      try {
        const response = await fetch(GET_PRAYERS_URL);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        // Assumi che lo script PHP restituisca un JSON con una proprietà 'total'
        // Esempio: { "total": 123 }
        const data = await response.json();
        if (typeof data.total === 'number') {
            setTotalPrayers(data.total); // Aggiorna lo stato con il valore reale
            setError(null); // Resetta eventuali errori precedenti
        } else {
             console.error("Formato dati preghiere non valido dal backend:", data);
             setError("Errore nel formato dati delle preghiere.");
             setTotalPrayers(0);
        }
      } catch (e) {
        console.error("Errore nel caricamento totale preghiere:", e);
        setError("Impossibile caricare il totale delle preghiere.");
        setTotalPrayers(0);
      }
    };

    fetchTotalPrayers(); // Chiama la funzione per ottenere il conteggio iniziale
    // Dipendenza vuota per eseguire solo al mount
  }, []);

  // Effetto 3: Animazione del contatore delle preghiere
  useEffect(() => {
    if (displayedPrayers < totalPrayers) {
      const difference = totalPrayers - displayedPrayers;
      const increment = Math.max(1, Math.ceil(difference / 20));
      const timeout = difference > 50 ? 20 : 50;

      const timer = setTimeout(() => {
        setDisplayedPrayers(prev => Math.min(prev + increment, totalPrayers));
      }, timeout);
      return () => clearTimeout(timer);
    }
     if (displayedPrayers > totalPrayers) {
         setDisplayedPrayers(totalPrayers);
     }
  }, [displayedPrayers, totalPrayers]);


  // Funzione per scegliere un NUOVO cardinale casuale e salvarlo
  const scegliNuovoCardinale = () => {
    // Assicurati che i dati siano stati caricati e che l'array non sia vuoto
    if (cardinali.length === 0) {
        console.warn("Elenco cardinali non ancora caricato o vuoto.");
        setError("Impossibile scegliere un cardinale: elenco non disponibile.");
        return;
    }

    // Scegli un cardinale casuale
    const random = Math.floor(Math.random() * cardinali.length);
    const chosenCardinale = cardinali[random];

    // Salva il cardinale scelto in localStorage
    try {
        localStorage.setItem(SAVED_CARDINALE_STORAGE_KEY, JSON.stringify(chosenCardinale));
        // Aggiorna lo stato del cardinale salvato nel componente React
        setSavedCardinale(chosenCardinale);
        // Imposta il cardinale corrente per il modale (quello appena scelto)
        setCardinale(chosenCardinale);
        // Resetta lo stato di conferma per il nuovo cardinale
        setConfirmed(false);
        // Apri il modale
        setIsModalOpen(true);
        setError(null); // Resetta errori UI non relativi al fetch iniziale
    } catch (storageError) {
        console.error("Errore nel salvare il cardinale in localStorage:", storageError);
        setError("Errore nel salvare la tua scelta. Riprova.");
        // Non aprire il modale o gestire l'errore visivamente
        setCardinale(null); // Assicurati che il modale non mostri dati parziali
        setSavedCardinale(null); // Assicurati che lo stato interno rifletta l'errore di salvataggio
    }
  };

  // Funzione per visualizzare il cardinale salvato nel modale
  const visualizzaCardinaleSalvato = () => {
      if (savedCardinale) {
          // Imposta il cardinale corrente per il modale al cardinale salvato
          setCardinale(savedCardinale);
          // Resetta lo stato di conferma (potresti voler confermare di nuovo per questa sessione)
          setConfirmed(false);
          // Apri il modale
          setIsModalOpen(true);
          setError(null); // Resetta errori UI
      } else {
          // Questo caso non dovrebbe verificarsi se il bottone "Prega per il tuo Cardinale"
          // è mostrato solo quando savedCardinale è non nullo, ma è una buona pratica.
          console.warn("Tentativo di visualizzare cardinale salvato ma savedCardinale è null.");
          setError("Nessun Cardinale salvato trovato."); // Mostra un messaggio di errore all'utente
      }
  };


  // Funzione per confermare la preghiera e aggiornare il conteggio sul backend (MySQL)
  const confermaPreghiera = async () => {
    setLoading(true);
    setError(null); // Resetta errori specifici della conferma

    try {
      const response = await fetch(INCREMENT_PRAYERS_URL, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
          },
          // Se devi inviare dati (es. ID cardinale), includi un body (non richiesto dall'esempio attuale)
          // body: JSON.stringify({ cardinaleId: cardinale.id })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (typeof data.total === 'number') {
          // Aggiorna il totale delle preghiere con il nuovo valore dal backend
          setTotalPrayers(data.total);
          setConfirmed(true); // Mostra il messaggio di conferma
          // Opzionale: Potresti voler chiudere il modale automaticamente dopo la conferma
          // setTimeout(() => chiudiModal(), 2000); // Chiude dopo 2 secondi
      } else {
          console.error("Formato dati di incremento non valido dal backend:", data);
          setError("Errore nella risposta dal server durante la registrazione.");
          setConfirmed(false);
      }

    } catch (e) {
      console.error("Errore durante la conferma preghiera:", e);
      setError("Errore durante la registrazione della preghiera. Riprova più tardi.");
      setConfirmed(false);
    } finally {
      setLoading(false);
    }
  };

  const chiudiModal = () => {
    setIsModalOpen(false);
    // NON resettare cardinale = null qui se vuoi che il modale possa riaprirsi
    // velocemente con le stesse info (se non è stato scelto un nuovo cardinale).
    // setCardinale(null); // <-- EVITARE di fare questo qui
    // setConfirmed(false); // Potresti voler resettare la conferma, ma resettarla all'apertura è sufficiente
  };

  // Funzione per gestire il click sull'overlay del modale
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      chiudiModal();
    }
  };

  // Funzione per aprire/chiudere il menu off-canvas
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Funzione per gestire il click su "Scopri un nuovo Cardinale" quando uno è già salvato
  const handleScopriNuovoQuandoSalvato = () => {
      const confirmMessage = "Ti è stato già affidato un Cardinale per cui pregare. Vuoi scoprirne uno nuovo? Se prosegui, perderai la memorizzazione del precedente.";
      if (window.confirm(confirmMessage)) {
          // Rimuovi il vecchio cardinale salvato da localStorage e dallo stato React
          localStorage.removeItem(SAVED_CARDINALE_STORAGE_KEY);
          setSavedCardinale(null);
          // Ora procedi a sceglierne uno nuovo
          scegliNuovoCardinale(); // Chiama la funzione che sceglie, salva e apre il modale
      }
      // Se l'utente annulla, non fare nulla
  };


  return (
    // Il div principale è flex flex-col, quindi mt-auto sul footer lo spingerà in basso
    <div className="min-h-screen flex flex-col bg-gray-100">

      {/* Header */}
      <header className="bg-white shadow-md p-4 flex justify-between items-center">
        {/* Logo a sinistra */}
        <img
          src={logoMonastero}
          alt="Logo Monastero Wi-Fi"
          className="h-12 w-auto" // Regola l'altezza secondo necessità
        />
        {/* Bottone Hamburger a destra */}
        <button
          onClick={toggleMenu}
          className="text-gray-800 hover:text-blue-600 focus:outline-none"
          aria-label="Apri menu"
        >
          {/* Icona Hamburger (puoi usare un SVG o un'icona da libreria) */}
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
          </svg>
        </button>
      </header>

      {/* Contenuto principale centrato */}
      {/* flex-grow permette a main di occupare lo spazio disponibile, spingendo il footer in basso */}
      <main className="flex flex-col items-center justify-center flex-grow p-4">
        <h1 className="text-4xl font-bold mt-10 mb-4 text-center text-[#d90429]">
          Adozione Spirituale di un Cardinale
          <img
            src={cardinaleEmoji}
            alt="Emoji Cardinale"
            className="inline-block h-9 w-auto align-middle ml-2 mb-2"
          />
        </h1>
        <p className="text-center mb-6 max-w-md text-gray-700">
          I monaci e le monache Wi-Fi, spiritualmente uniti a Maria, pregheranno giornalmente lo Spirito Santo, da oggi al giorno dell’elezione del nuovo Papa, per ciascuno dei cardinali elettori, nell’attesa del nuovo successore di Pietro.
        </p>

        {/* Mostra messaggi di stato per il caricamento dei dati */}
        {loadingCardinali && <p className="text-gray-600 mb-4">Caricamento dati...</p>}
        {errorLoadingCardinali && <p className="text-red-500 mb-4">{errorLoadingCardinali}</p>}
        {/* Mostra errori relativi al conteggio preghiere o alla conferma */}
        {error && <p className="text-red-500 mb-4">{error}</p>}

        {/* Logica di visualizzazione dei bottoni basata su savedCardinale */}
        {savedCardinale ? (
            <div className="flex flex-col items-center space-y-4">
                <button
                    onClick={visualizzaCardinaleSalvato} // Funzione per mostrare il modale del cardinale salvato
                    // Modifica classi Tailwind per i colori specificati
                    className={`bg-[#d90429] hover:bg-[#a90321] text-white font-bold py-3 px-6 rounded-full transition duration-300 ${loadingCardinali || errorLoadingCardinali || cardinali.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={loadingCardinali || errorLoadingCardinali || cardinali.length === 0}
                >
                    Prega per il tuo Cardinale
                </button>
                 <button
                    onClick={handleScopriNuovoQuandoSalvato} // Funzione per scegliere un nuovo cardinale con alert
                    // Modifica classi Tailwind per i colori specificati (usando un colore leggermente più chiaro per il base)
                    // Sostituisci '#ef233c' con un hex code che preferisci per il "rosso chiaro"
                    className={`bg-[#ef233c] hover:bg-[#d90429] text-white font-bold py-3 px-6 rounded-full transition duration-300 ${loadingCardinali || errorLoadingCardinali || cardinali.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={loadingCardinali || errorLoadingCardinali || cardinali.length === 0}
                >
                    Scopri un nuovo Cardinale
                </button>
            </div>
        ) : (
            // Stato iniziale: nessun cardinale salvato
             <button
                onClick={scegliNuovoCardinale} // Funzione per scegliere e salvare il primo cardinale
                // Classe originale per il primo bottone
                className={`bg-[#d90429] hover:bg-[#a90321] text-white font-bold py-3 px-6 rounded-full transition duration-300 ${loadingCardinali || errorLoadingCardinali || cardinali.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={loadingCardinali || errorLoadingCardinali || cardinali.length === 0}
            >
                {loadingCardinali ? 'Caricamento...' : 'Scopri il tuo Cardinale'}
            </button>
        )}


        <div className="mt-10 text-center">
          <p className="text-xl font-semibold text-gray-800 transition-all duration-500 ease-out">
            Totale preghiere per i Cardinali effettuate:
          </p>
          <p className="text-xl font-semibold text-gray-800 transition-all duration-500 ease-out">
              <span className="inline-block transition-transform duration-500 ease-out">{displayedPrayers}</span>
          </p>
           {/* Mostra un messaggio di caricamento se il conteggio iniziale è ancora in fetch */}
           {/* Questo messaggio è meno critico dato che i cardinali e il salvataggio sono più prioritari */}
           {/* Potresti volerlo rimuovere o integrarlo nell'errore generale */}
           {/* {loadingCardinali && <p className="text-gray-600">Caricamento conteggio preghiere...</p>} */}
        </div>

        <p className="text-center mt-5 mb-6 max-w-md text-gray-700">
          Premi il bottone per vedere il Cardinale che ti verrà affidato casualmente. Un contatore mostrerà quante persone hanno pregato fino a questo momento. Sarà possibile visualizzare chi è il cardinale che è stato scelto in maniera random e si reciterà la preghiera mostrata.
        </p>

      </main>


      {/* --- INIZIO FOOTER --- */}
      <footer className="mt-auto py-6 bg-gray-200 flex flex-col items-center w-full">
        <img
          src={logoMonastero}
          alt="Logo Monastero Wi-Fi Footer"
          className="h-10 w-auto mb-2" // Altezza ridotta per il footer
        />
        <p className="text-sm text-gray-700 mb-1 font-semibold">
          Monastero Wi-Fi
        </p>
        {/* Potresti voler rendere questo un link in futuro */}
        <p className="text-xs text-gray-600">
          <a
            href="https://www.monasterowi-fi.it/monasteri-wifi-in-italia/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
            >Scopri i Monasteri Wi-Fi in Italia</a>
        </p>
      </footer>
      {/* --- FINE FOOTER --- */}


      {/* Modale Cardinale */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
          onClick={handleOverlayClick}
        >
          <div
            className="bg-white p-6 rounded-2xl shadow-2xl max-w-md w-full max-h-[calc(100vh-100px)] overflow-y-auto animate-fade-in relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={chiudiModal}
              className="absolute top-2 right-4 text-gray-500 hover:text-gray-800 text-2xl font-bold"
              aria-label="Chiudi modale"
            >
              &times;
            </button>

            {cardinale && ( // Mostra i dettagli del cardinale attualmente impostato (potrebbe essere salvato o nuovo)
              <div className="flex flex-col items-center mt-10">
                <div className="flex flex-col items-center mt-2 mb-4 rounded-2xl shadow-md p-4 bg-[#d90429]">
                    {/* Messaggio dinamico nel modale */}
                    <p className="mb-1 text-white italic text-center">
                       {cardinale === savedCardinale ? "Puoi pregare per:" : "Hai 'adottato' spiritualmente:"}
                    </p>
                    <h2 className="text-2xl font-bold mb-2 text-white text-center">{cardinale.nome} {cardinale.cognome}</h2>
                    <p className="mb-1 text-white"><strong>Paese:</strong> {cardinale.paese}</p>
                    <p className="mb-1 text-white"><strong>Nascita:</strong> {cardinale.data_nascita}</p>
                    <p className="mb-1 text-white text-center"><strong>Ruolo:</strong> {cardinale.ruolo}</p>
                </div>
                <p className="italic mb-4 text-center text-gray-500 whitespace-pre-line">
                   <b>Veni Creator 🙏🏻</b><br></br>
                    Vieni, o Spirito creatore,
                    visita le nostre menti,
                    riempi della tua grazia
                    i cuori che hai creato.<br></br><br></br>
                    O dolce consolatore,
                    dono del Padre altissimo,
                    acqua viva, fuoco, amore,
                    santo crisma dell'anima.<br></br><br></br>
                    Dito della mano di Dio,
                    promesso dal Salvatore,
                    irradia i tuoi sette doni,
                    suscita in noi la parola.<br></br><br></br>
                    Sii luce all'intelletto,
                    fiamma ardente nel cuore;
                    sana le nostre ferite
                    col balsamo del tuo amore.<br></br><br></br>
                    Difendici dal nemico,
                    reca in dono la pace,
                    la tua guida invincibile
                    ci preservi dal male.<br></br><br></br>
                    Luce d'eterna sapienza,
                    svelaci il grande mistero
                    di Dio Padre e del Figlio
                    uniti in un solo Amore. Amen.<br></br><br></br>
                    Preghiamo.<br></br>
                    O Padre, che guidi e custodisci la tua Chiesa,
                    dona ai Padri Cardinali
                    lo Spirito di intelligenza, di verità, di pace,
                    perché si sforzino di conoscere la tua volontà,
                    e ti servano con totale dedizione.
                    Per Cristo nostro Signore. Amen.<br></br>
                </p>
                {!confirmed ? (
                  <button
                    onClick={confermaPreghiera}
                    className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-full transition duration-300 disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Registrazione...' : 'Conferma di aver pregato per questo Cardinale!'}
                  </button>
                ) : (
                  <p className="text-green-600 font-semibold mt-4">Preghiera confermata! Grazie!</p>
                )}
                <button
                  onClick={chiudiModal}
                  className="mt-4 text-gray-500 hover:text-gray-700"
                >
                  Chiudi
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overlay del menu off-canvas (rimane invariato) */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={toggleMenu}
        ></div>
      )}

      {/* Menu Off-Canvas (rimane invariato) */}
      <div
        className={`fixed top-0 right-0 w-64 bg-white h-full shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="p-4">
          <button
            onClick={toggleMenu}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl font-bold"
            aria-label="Chiudi menu"
          >
            &times;
          </button>
          <h3 className="text-xl font-semibold mb-4 text-gray-800">Menu</h3>
          <ul>
            <li>
              <a
                href="https://monasterowi-fi.it/"
                target="_blank"
                rel="noopener noreferrer"
                className="block py-2 text-blue-600 hover:text-blue-800"
                onClick={toggleMenu}
              >
                Monastero Wi-Fi
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Stile per l'animazione del modal (rimane invariato) */}
      <style>{`
        @keyframes fade-in {
          0% { opacity: 0; transform: scale(0.95); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>

    </div> // Chiusura del div principale
  );
}

export default Home;